import { IconButton } from "@mui/material";
import Box from "@mui/material/Box";
import {
  DataGrid,
  GridColDef,
  GridColumnHeaderParams,
  GridColumnVisibilityModel,
  GridRowId,
  GridRowsProp,
  GridValidRowModel,
} from "@mui/x-data-grid";
import * as React from "react";
import { useEffect } from "react";
import { FaPlus, FaTrash } from "react-icons/fa";
import { MdOutlineLock, MdEdit } from "react-icons/md";

import {
  dropdownFieldsDataProps,
  fieldMapProps,
  validFieldPatternsProps,
} from "../../@types/assetTypes/ap";
import DeleteExpenseView from "../DeleteExpenseView";
import DuplicateExpenseView from "../DuplicateExpenseView";
import ConfirmModal from "../Modalwindow";
import QuickSearchToolbar from "../SearchToolbar";
import Tooltip from "../Tooltip";
import UpdateExpenseView from "../UpdateExpenseView";

interface TableProps {
  suffix: string;
  cols: GridColDef[];
  rows: GridRowsProp;
  fieldTypes?: {
    [k: string]:
      | "dropdown"
      | "multi-select-dropdown"
      | "search-dropdown"
      | "textarea";
  };
  primaryField: string;
  mandatoryFields: string[];
  upperCaseFields?: string[];
  hideFields: string[];
  hideAddFields?: string[];
  disableUpdateFields?: string[];
  hideAdd?: boolean;
  hideDelete?: boolean;
  disableAdd?: boolean;
  disableCSV?: boolean;
  duplicateCheckFields?: string[];
  freezeColumnsLeft?: string[];
  freezeColumnsRight?: string[];
  dropdownFieldsData?: dropdownFieldsDataProps[];
  fieldMap?: fieldMapProps;
  validFieldPatterns?: validFieldPatternsProps;
  isShowActionsColumn?: boolean;
  csvFileName?: string;
  onRowIdChange?: (rowId: GridRowId | null) => void;
  onUpdate?: (
    rowId: GridRowId | null,
    data: { [k: string]: string | object },
  ) => Promise<void>;
  onDelete?: (id: GridRowId) => Promise<void>;
  HeaderComponent?: JSX.Element;
  FooterComponent?: JSX.Element;
}

const Table: React.FC<TableProps> = ({
  suffix,
  rows,
  cols,
  fieldTypes,
  primaryField,
  mandatoryFields,
  hideFields,
  hideAddFields,
  upperCaseFields,
  disableUpdateFields,
  hideAdd,
  hideDelete,
  disableAdd,
  disableCSV,
  duplicateCheckFields,
  freezeColumnsLeft,
  freezeColumnsRight,
  dropdownFieldsData,
  fieldMap,
  validFieldPatterns,
  isShowActionsColumn,
  csvFileName,
  onRowIdChange,
  onUpdate,
  onDelete,
  HeaderComponent,
  FooterComponent,
}) => {
  const [rowId, setRowId] = React.useState<GridRowId>("");
  const [deleteRowId, setDeleteRowId] = React.useState<GridRowId>("");
  const [duplicateRowId, setDuplicateRowId] = React.useState<GridRowId>("");

  const [columnVisibilityModel, setColumnVisibilityModel] =
    React.useState<GridColumnVisibilityModel>({});

  const [selectedDensity, setSelectedDensity] = React.useState<
    "compact" | "standard" | "comfortable"
  >("standard");

  const rowIdRef = React.useRef<GridRowId | null>(null);
  const updateRef = React.useRef<{ [k: string]: string | object } | null>(null);

  const rowToUpdate: GridValidRowModel | undefined | null =
    updateRef.current || rows.find(({ id }) => rowId === id);

  const rowToDelete: GridValidRowModel | undefined = rows.find(
    ({ id }) => deleteRowId === id,
  );

  const rowDuplicate: GridValidRowModel | undefined = rows.find(
    ({ id }) => duplicateRowId === id,
  );

  const rowToDeletePrimaryField = rowToDelete?.[primaryField];
  const rowDuplicatePrimaryField = rowDuplicate?.[primaryField];
  const primaryFieldType = fieldTypes?.[primaryField];

  let deleteKey;
  let duplicateKey;

  if (typeof rowToDeletePrimaryField === "object") {
    if (primaryFieldType === "multi-select-dropdown")
      deleteKey = rowToDeletePrimaryField?.join("") || "";
    else if (primaryFieldType === "search-dropdown")
      deleteKey = rowToDeletePrimaryField?.value || "";
    else deleteKey = rowToDeletePrimaryField?.id || "";
  } else {
    deleteKey = rowToDeletePrimaryField || "";
  }

  if (typeof rowDuplicatePrimaryField === "object") {
    if (primaryFieldType === "multi-select-dropdown")
      duplicateKey = rowDuplicatePrimaryField?.join("") || "";
    else if (primaryFieldType === "search-dropdown")
      duplicateKey = rowDuplicatePrimaryField?.value || "";
    else duplicateKey = rowDuplicatePrimaryField?.id || "";
  } else {
    duplicateKey = rowDuplicatePrimaryField || "";
  }

  const _dropdownFieldsData = [...(dropdownFieldsData || [])];

  const i = _dropdownFieldsData?.findIndex(
    ({ field }) => field === "pairedSpecialExpense",
  );

  if (i > -1) {
    const pseDropdown = { ..._dropdownFieldsData[i] };

    pseDropdown.data = pseDropdown.data?.filter(
      ({ id }) => id !== rowToUpdate?.id,
    );

    _dropdownFieldsData.splice(i, 1, pseDropdown);
  }

  const invisibleColumnIds = Object.keys(columnVisibilityModel ?? {}).filter(
    (k) => !columnVisibilityModel?.[k],
  );

  const handleAddClick = (): void => {
    updateRef.current = null;
    setRowId("__open__");
  };

  const handleEditClick = (id: GridRowId) => () => {
    updateRef.current = null;
    setRowId(id);
  };

  const handleDeleteClick = (id: GridRowId) => () => {
    updateRef.current = null;
    setDeleteRowId(id);
  };

  const handleUpdate =
    (rowId: GridRowId | null) =>
    (data: { [k: string]: string | object }): Promise<void> => {
      const getDuplicateCheckFieldsValue = (
        row: GridValidRowModel,
      ): string[] | undefined => {
        return duplicateCheckFields?.map((field) => {
          if (typeof row[field] === "object") {
            if (primaryFieldType === "multi-select-dropdown") {
              const value = row[field] as Array<number | string>;

              return value.join("");
            } else if (primaryFieldType === "search-dropdown") {
              const value = row[field] as { value: string };

              return value.value;
            } else {
              const value = row[field] as { id: string };

              return value.id;
            }
          } else {
            return row[field];
          }
        });
      };

      const duplicateRow: GridValidRowModel | undefined = rows.find((r) => {
        if (rowId === r.id) return false;
        const current = getDuplicateCheckFieldsValue(r)?.join("");
        const selected = getDuplicateCheckFieldsValue(data)?.join("");

        if (!current || !selected) return false;

        return current === selected;
      });

      updateRef.current = data;
      rowIdRef.current = rowId;

      if (duplicateRow) {
        setRowId("");
        setDuplicateRowId(duplicateRow?.id || "");

        return Promise.resolve();
      } else if (onUpdate) {
        return onUpdate(rowId === "__open__" ? null : rowId, data)
          .then(() => {
            updateRef.current = null;
            rowIdRef.current = null;
            setRowId("");
          })
          .catch(() => {});
      } else {
        return Promise.resolve();
      }
    };

  const handleUpdateYes = (rowId: GridRowId | null) => (): Promise<void> => {
    if (onUpdate) {
      return onUpdate(rowId, updateRef.current || {})
        .then(() => {
          updateRef.current = null;
          rowIdRef.current = null;
          setDuplicateRowId("");
        })
        .catch(() => {});
    } else {
      return Promise.resolve();
    }
  };

  const handleUpdateNo = (): void => {
    setRowId(rowIdRef.current || "__open__");
    setDuplicateRowId("");
  };

  const handleDelete = (): Promise<void> => {
    if (onDelete) {
      return onDelete(deleteRowId)
        .then(() => {
          setDeleteRowId("");
        })
        .catch(() => {});
    } else {
      return Promise.resolve();
    }
  };

  const _freezeColumnsLeft = (freezeColumnsLeft || []).filter(
    (id) => !invisibleColumnIds.includes(id),
  );

  const _freezeColumnsRight = (freezeColumnsRight || []).filter(
    (id) => !invisibleColumnIds.includes(id),
  );

  const _isShowActionsColumn =
    isShowActionsColumn && !invisibleColumnIds.includes("actions");

  const _freezeColumns = [
    ..._freezeColumnsLeft,
    ..._freezeColumnsRight,
    _isShowActionsColumn ? "actions" : "",
  ].filter(Boolean);

  const frozenColumnHeader = (params: GridColumnHeaderParams): JSX.Element => {
    return (
      <div className="flex text-sm items-center gap-2 font-bold relative">
        {params.colDef.headerName}
        <Tooltip
          type={2}
          placement="top"
          rightIcon={<MdOutlineLock size={17} />}
          title={`${params.colDef.headerName || "This"} column is frozen and stays visible while scrolling.`}
        />
      </div>
    );
  };

  const columns = cols.filter(Boolean).map((c) => {
    if (!_freezeColumns.includes(c.field)) return c;

    return {
      ...c,
      renderHeader: frozenColumnHeader,
    };
  });

  const columnsWithActionColumn = [
    ...columns,
    {
      field: "actions",
      type: "actions" as const,
      headerName: "Actions",
      width: 100,
      cellClassName: "actions",
      renderHeader: frozenColumnHeader,
      getActions: (params: { id: string }): JSX.Element[] => {
        const { id } = params;

        const a = [
          <IconButton
            key={`${id}-edit`}
            size="small"
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          >
            <MdEdit />
          </IconButton>,
        ];

        if (!hideDelete) {
          a.push(
            <IconButton
              key={`${id}-delete`}
              onClick={handleDeleteClick(id)}
              color="inherit"
            >
              <FaTrash fontSize="small" />
            </IconButton>,
          );
        }

        return a;
      },
      filterable: false,
    },
  ];

  const tableCSS: Record<string, React.CSSProperties> = {};

  _freezeColumns.forEach((id, i) => {
    const prevCol = columnsWithActionColumn.find(
      (c) => c.field === _freezeColumns[i - 1],
    );

    const nextCol = columnsWithActionColumn.find(
      (c) => c.field === _freezeColumns[i + 1],
    );

    const style: React.CSSProperties = {
      position: "sticky",
      left: _freezeColumnsLeft.includes(id)
        ? ((prevCol as GridColDef)?.width ??
          (prevCol as GridColDef)?.minWidth ??
          0)
        : 0,
      right: _freezeColumnsRight.includes(id)
        ? ((nextCol as GridColDef)?.width ??
          (nextCol as GridColDef)?.minWidth ??
          0)
        : 0,
    };

    const boxShadow = (type: "header" | "row"): string => {
      const style = {
        header: {
          left: "2px 0 4px rgba(255, 255, 255, 0.1)",
          right: "-2px 0 4px rgba(255, 255, 255, 0.1)",
        },
        row: {
          left: "2px 0 4px rgba(0, 0, 0, 0.1)",
          right: "-2px 0 4px rgba(0, 0, 0, 0.1)",
        },
      };

      return _freezeColumnsLeft.length > 0 &&
        _freezeColumnsLeft.findIndex((_id) => _id === id) ===
          _freezeColumnsLeft.length - 1
        ? style[type].left
        : [..._freezeColumnsRight, _isShowActionsColumn ? "actions" : ""]
              .filter(Boolean)
              .findIndex((_id) => _id === id) === 0
          ? style[type].right
          : "";
    };

    tableCSS[`& .MuiDataGrid-columnHeader[data-field="${id}"]`] = {
      ...style,
      zIndex: 200,
      boxShadow: boxShadow("header"),
    };
    tableCSS[`& .MuiDataGrid-cell[data-field="${id}"]`] = {
      ...style,
      backgroundColor: "white",
      zIndex: 1,
      boxShadow: boxShadow("row"),
    };
    tableCSS[`& .MuiDataGrid-row:hover .MuiDataGrid-cell[data-field="${id}"]`] =
      {
        backgroundColor: "#f5f5f5",
      };
  });

  useEffect(() => {
    if (onRowIdChange) onRowIdChange(rowId);
  }, [rowId, onRowIdChange]);

  useEffect(() => {}, []);

  return (
    <div className="w-full space-y-5">
      <div className="flex items-center justify-end">
        {HeaderComponent}
        {isShowActionsColumn && !hideAdd && (
          <button
            className="ml-5 my-1 cursor-pointer text-[white] text-sm font-bold w-fit flex items-center rounded px-3 py-1 bg-[#211551] space-x-2 disabled:bg-opacity-20 disabled:cursor-default"
            onClick={handleAddClick}
            disabled={disableAdd}
          >
            <span>Add {suffix}</span>
            <FaPlus />
          </button>
        )}
      </div>
      <Box
        sx={{
          height: 500,
          width: "100%",
          "& .actions": {
            color: "text.secondary",
          },
          "& .textPrimary": {
            color: "text.primary",
          },
        }}
      >
        <DataGrid
          initialState={{
            sorting: {
              sortModel: [{ field: primaryField, sort: "asc" }],
            },
            pagination: { paginationModel: { page: 0, pageSize: 20 } },
          }}
          rows={rows}
          columns={isShowActionsColumn ? columnsWithActionColumn : columns}
          slots={{
            toolbar: () => (
              <QuickSearchToolbar
                processName={csvFileName || suffix}
                disableCSV={disableCSV}
              />
            ),
          }}
          pageSizeOptions={[20, 50, 100]}
          pagination
          density={selectedDensity}
          sx={{
            "& .MuiDataGrid-cell": {
              display: "flex",
              alignItems: "center",
              "&:hover": {
                cursor: "pointer",
              },
            },
            "& .MuiDataGrid-toolbarContainer": {
              "& .MuiButtonBase-root": { color: "#211551" },
            },

            "& .MuiDataGrid-columnHeaders": {
              "--DataGrid-containerBackground": "#211551",
              bgcolor: "#211551",
              color: "white",
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: "bold",
              },
              "& .MuiIconButton-sizeSmall": {
                color: "white",
              },
            },
            "& .MuiDataGrid-columnHeader": { bgcolor: "#211551 !important" },
            "& .MuiDataGrid-row": { fontSize: "small" },
            "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
              py: "4px",
            },
            "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
              py: "10px",
            },
            "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
              py: "17px",
            },
            "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
              outline: "none",
            },
            "& .MuiDataGrid-columnHeader:focus, & MuiDataGrid-columnHeader:focus-within":
              {
                outline: "none",
              },
            ...tableCSS,
          }}
          getRowHeight={() => "auto"}
          isCellEditable={() => false}
          columnHeaderHeight={44}
          disableRowSelectionOnClick
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
          onDensityChange={(density) => setSelectedDensity(density)}
        />
        {rowId !== "" && (
          <ConfirmModal closeModal={() => setRowId("")}>
            <UpdateExpenseView
              title={rowId === "__open__" ? `Add ${suffix}` : `Edit ${suffix}`}
              fields={columns}
              fieldTypes={fieldTypes}
              mandatoryFields={mandatoryFields}
              upperCaseFields={upperCaseFields}
              hideFields={
                rowId === "__open__"
                  ? [...hideFields, ...(hideAddFields || [])]
                  : hideFields
              }
              disableFields={rowId === "__open__" ? [] : disableUpdateFields}
              dropdownFieldsData={_dropdownFieldsData}
              fieldMap={fieldMap}
              validFieldPatterns={validFieldPatterns}
              data={rowToUpdate}
              onClose={() => setRowId("")}
              onSubmit={handleUpdate(rowId)}
            />
          </ConfirmModal>
        )}
        {deleteRowId !== "" && (
          <ConfirmModal closeModal={() => setDeleteRowId("")}>
            <DeleteExpenseView
              title={`Delete ${suffix}`}
              description={[
                `Are you sure you want to delete`,
                deleteKey ? ` "${deleteKey}"` : "",
                "?",
              ].join("")}
              onClose={() => setDeleteRowId("")}
              onSubmit={handleDelete}
            />
          </ConfirmModal>
        )}
        {duplicateRowId !== "" && (
          <ConfirmModal closeModal={handleUpdateNo}>
            <DuplicateExpenseView
              title={`Duplicate Found`}
              description={[
                duplicateKey ? ` "${duplicateKey}"` : "",
                columns.length > 1
                  ? `is existing, would you like to update?`
                  : `is already present.`,
              ].join(" ")}
              onClose={handleUpdateNo}
              onYes={
                columns.length > 1 ? handleUpdateYes(duplicateRowId) : undefined
              }
              onNo={handleUpdateNo}
              noText={columns.length > 1 ? undefined : "Ok"}
            />
          </ConfirmModal>
        )}
      </Box>
      {FooterComponent}
    </div>
  );
};

export default Table;
